import { AgGridAngular } from '@ag-grid-community/angular';
import { AllModules } from '@ag-grid-enterprise/all-modules';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material';
import { ActivatedRoute, Router } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { select, Store } from '@ngrx/store';
import { JournalEntry } from 'app/api/journal.api.service';
import { selectAccountingByJournal } from 'app/main/accounting/accounting.selectors';
import { CompaniesUISelectors } from 'app/main/company/selectors';
import { AppState } from 'app/reducers';
import { agGridLocale, EurCurrencyCellRenderer } from 'app/utils/ag-grid.locale';
import { AmplifyService } from 'aws-amplify-angular';
import * as moment from 'moment';
import { Observable, Subject } from 'rxjs';
import { debounceTime, delay, distinctUntilChanged, filter, map, take, takeUntil, tap } from 'rxjs/operators';

import { SnackbarOpen } from '../shared/snackbar/snackbar.actions';
import { JournalEntryUpdateRequested, JournalQuery } from './journal.actions';
import { selectJournal, selectJournalError, selectJournalLoading } from './journal.selectors';

@Component({
    selector: 'journal',
    templateUrl: './journal.component.html',
    styleUrls: ['./journal.component.scss'],
    animations: fuseAnimations,
    encapsulation: ViewEncapsulation.None
})
export class JournalComponent implements OnInit, OnDestroy, AfterViewInit {
    public gridApi;
    public gridColumnApi;

    public columnDefs;
    public defaultColDef;
    public statusBar;
    public localeText;
    public detailCellRendererParams;
    public masterDetail;
    public getRowHeight;
    public modules;

    keyUp = new Subject<KeyboardEvent>();

    rowData$: Observable<JournalEntry[]>;
    loading$: Observable<boolean>;

    @ViewChild('agGrid', { static: false }) agGrid: AgGridAngular;

    // Protected
    protected logger: any;

    // Private
    private _unsubscribeAll: Subject<any>;

    constructor(
        protected amplifyService: AmplifyService,
        protected router: Router,
        protected activatedRoute: ActivatedRoute,
        protected store: Store<AppState>,
        protected dialog: MatDialog
    ) {
        const instance = this;

        this.logger = this.amplifyService.logger('JournalComponent');
        this._unsubscribeAll = new Subject();
        this.modules = AllModules;
        this.localeText = agGridLocale.fr;
        this.columnDefs = [
            {
                headerName: 'Clé',
                field: 'key',
                minWidth: 150,
                cellRenderer: 'agGroupCellRenderer'
            },
            { headerName: 'Nom', field: 'name', minWidth: 150, editable: true }
        ];
        this.defaultColDef = {
            enableValue: true,
            sortable: true,
            filter: true,
            resizable: true
        };

        this.statusBar = {
            statusPanels: [
                {
                    statusPanel: 'agTotalRowCountComponent',
                    align: 'left'
                },
                { statusPanel: 'agSelectedRowCountComponent' },
                { statusPanel: 'agFilteredRowCountComponent' }
            ]
        };
        this.masterDetail = true;
        this.detailCellRendererParams = {
            detailGridOptions: {
                localeText: agGridLocale.fr,
                columnDefs: [
                    {
                        headerName: 'Date',
                        field: 'entryDate',
                        valueGetter: params =>
                            params.data
                                ? moment(params.data.entryDate).format(
                                    'YYYY-MM'
                                )
                                : ''
                    },
                    { headerName: 'Journal', field: 'journal' },
                    { headerName: 'Compte', field: 'account' },
                    { headerName: 'Auxiliaire', field: 'auxiliary' },
                    { headerName: 'Libellé', field: 'entryLabel' },
                    {
                        headerName: 'Débit',
                        field: 'debit',
                        type: 'numericColumn',
                        cellRenderer: EurCurrencyCellRenderer
                    },
                    {
                        headerName: 'Crédit',
                        field: 'credit',
                        type: 'numericColumn',
                        cellRenderer: EurCurrencyCellRenderer
                    }
                ],
                onFirstDataRendered(params): void {
                    instance.autoSizeAll(params);
                },
                // onGridReady: params => params.api.setDomLayout('autoHeight')
            },
            getDetailRowData: (params: any): void => {
                const key = params.data.key;
                instance.store
                    .pipe(
                        select(selectAccountingByJournal(key)),
                        delay(0),
                        take(1)
                    )
                    .subscribe(entries => {
                        params.successCallback(entries);
                    });
            }
        };
    }

    ngOnInit(): void {
        this.loading$ = this.store.pipe(select(selectJournalLoading));

        this.store
            .pipe(
                takeUntil(this._unsubscribeAll),
                select(CompaniesUISelectors.selectCurrentCompanyId),
            )
            .subscribe(companyId => {
                window.history.pushState({}, '', `/journal/${companyId}`);
            });

        this.store
            .pipe(
                takeUntil(this._unsubscribeAll),
                select(selectJournalError),
                filter(message => message !== null),
            )
            .subscribe(message => {
                this.store.dispatch(new SnackbarOpen({
                    message, action: 'OK', config: { panelClass: ['snackbar-error'] }
                }));
            });

        this.keyUp
            .pipe(
                takeUntil(this._unsubscribeAll),
                map((event: any) => event.target.value),
                debounceTime(250),
                distinctUntilChanged(),
                tap(search => this.gridApi.setQuickFilter(search))
            )
            .subscribe();

        this.rowData$ = this.store.pipe(select(selectJournal));
    }

    onGridReady(params): void {
        this.gridApi = params.api;
        this.gridColumnApi = params.columnApi;
    }

    public clearSearch(): void {
        this.gridApi.setQuickFilter('');
    }

    onFirstDataRendered(params): void {
        this.autoSizeAll(params);
    }

    onCellValueChanged(ev: any): void {
        const entry = ev.data as JournalEntry;
        entry.name = entry.name ? entry.name.toUpperCase() : entry.name;
        this.store.dispatch(new JournalEntryUpdateRequested({ entry }));
    }

    private autoSizeAll(params): void {
        const allColumnIds = params.columnApi
            .getAllColumns()
            .map(column => column.colId);
        params.columnApi.autoSizeColumns(allColumnIds);
    }

    ngAfterViewInit(): void { }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }
}
